import React from 'react'
import { Crown, Trophy } from 'lucide-react'

import { useTranslation } from '../../hooks/use-i18n'
import { formatPoints } from '../../utils/points-label'

interface ArenaPlayerCardProps {
  name: string
  rank?: number
  points?: number
  wins?: number
  draws?: number
  losses?: number
  isPlaying?: boolean
  lightTheme?: boolean
  accentColor?: string
}

export const ArenaPlayerCard: React.FC<ArenaPlayerCardProps> = ({
  name,
  rank,
  points = 0,
  wins = 0,
  draws = 0,
  losses = 0,
  isPlaying = false,
  lightTheme = false,
  accentColor,
}) => {
  const { t } = useTranslation()
  return (
    <div
      className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl transition-all duration-300 ${
        lightTheme
          ? 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
          : 'bg-secondary/50 hover:bg-secondary border border-border'
      } ${isPlaying ? (lightTheme ? 'ring-2 ring-indigo-900' : 'ring-2 ring-primary') : ''}`}
    >
      {rank != null && (
        <div
          className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full font-bold text-xs sm:text-sm flex-shrink-0 ${
            rank === 1 ? 'bg-primary text-primary-foreground' : ''
          } ${rank === 2 ? 'bg-muted-foreground/30 text-foreground' : ''} ${
            rank === 3 ? 'bg-amber-700/50 text-foreground' : ''
          } ${rank > 3 ? (lightTheme ? 'bg-gray-200 text-gray-700' : 'bg-muted text-muted-foreground') : ''}`}
        >
          {rank <= 3 ? <Crown className="w-3 h-3 sm:w-4 sm:h-4" /> : rank}
        </div>
      )}
      <div
        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center overflow-hidden border-2 flex-shrink-0 ${
          lightTheme ? 'bg-gray-200 border-gray-300' : 'bg-muted border-border'
        }`}
      >
        <span className={`text-sm sm:text-lg font-semibold ${lightTheme ? 'text-gray-700' : ''}`}>
          {(name || '?')[0].toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate text-sm sm:text-base ${lightTheme ? 'text-gray-900' : ''}`}>{name || 'Player'}</p>
        <p className={`text-xs sm:text-sm flex items-center gap-1 sm:gap-2 ${lightTheme ? 'text-gray-600' : 'text-muted-foreground'}`}>
          <Trophy className={`w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0 ${lightTheme ? 'text-amber-500' : 'text-primary'}`} style={accentColor ? { color: accentColor } : undefined} />
          {formatPoints(Number(points), t)}
          {(wins > 0 || draws > 0 || losses > 0) && (
            <span className="text-[10px] sm:text-xs hidden sm:inline">
              ({[wins > 0 && `${wins}W`, draws > 0 && `${draws}D`, losses > 0 && `${losses}L`].filter(Boolean).join(' / ')})
            </span>
          )}
        </p>
      </div>
      {isPlaying && (
        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full flex-shrink-0 ${lightTheme ? 'bg-indigo-100 text-indigo-900' : 'bg-primary/20 text-primary'}`}>
          Playing
        </span>
      )}
    </div>
  )
}
